// src/components/common/ConfirmDialog.tsx
import {Dialog, Transition} from "@headlessui/react";
import {Fragment} from "react";
import {AlertTriangle, Info, XCircle} from "lucide-react";
import {useConfirm} from "../../hooks/common/useConfirm";
import {Button} from "../ui";
import {clsx} from "clsx";

const ConfirmDialog: React.FC = () => {
  const {confirmDialog} = useConfirm();

  const getIcon = () => {
    switch (confirmDialog.variant) {
      case "danger":
        return <XCircle className="w-6 h-6 text-danger-600" />;
      case "warning":
        return <AlertTriangle className="w-6 h-6 text-warning-600" />;
      default:
        return <Info className="w-6 h-6 text-blue-600" />;
    }
  };

  const getIconBgColor = () => {
    switch (confirmDialog.variant) {
      case "danger":
        return "bg-danger-100 dark:bg-danger-900/20";
      case "warning":
        return "bg-warning-100 dark:bg-warning-900/20";
      default:
        return "bg-blue-100 dark:bg-blue-900/20";
    }
  };

  const handleConfirm = () => {
    confirmDialog.onConfirm?.();
  };

  const handleCancel = () => {
    confirmDialog.onCancel?.();
  };

  return (
    <Transition appear show={confirmDialog.isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-dark-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="sm:flex sm:items-start">
                  <div
                    className={clsx(
                      "mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10",
                      getIconBgColor()
                    )}
                  >
                    {getIcon()}
                  </div>

                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                    >
                      {confirmDialog.title}
                    </Dialog.Title>

                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {confirmDialog.message}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:space-x-2 sm:space-x-reverse space-y-2 sm:space-y-0">
                  <Button
                    variant={
                      confirmDialog.variant === "danger" ? "danger" : "primary"
                    }
                    onClick={handleConfirm}
                    className="w-full sm:w-auto"
                  >
                    {confirmDialog.confirmText || "Confirm"}
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    className="w-full sm:w-auto"
                  >
                    {confirmDialog.cancelText || "Cancel"}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmDialog;
